import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../api/axios';
import { useChampionTheme, TrophyIcon } from '../../components/ChampionElements';

export default function EpisodeList() {
  const { webinarId } = useParams();
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const t = useChampionTheme();

  const podiumColors = [t.gold, t.silver, t.bronze];

  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        const res = await api.get(`/api/member/webinars/${webinarId}/episodes`);
        setEpisodes(res.data);
      } catch (err) {
        setError('Gagal memuat episode.');
      } finally {
        setLoading(false);
      }
    };
    fetchEpisodes();
  }, [webinarId]);

  const handleMarkComplete = async (episodeId) => {
    try {
      await api.post(`/api/member/episodes/${episodeId}/complete`);
      setEpisodes(prev =>
        prev.map(ep =>
          ep.id === episodeId ? { ...ep, is_completed: true, completed_at: new Date().toISOString() } : ep
        )
      );
    } catch (err) {
      alert('Gagal menandai episode selesai.');
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <TrophyIcon size={48} />
        <p className="mt-3" style={{ color: t.gold }}>Memuat episode...</p>
      </div>
    );
  }

  if (error) {
    return <div className="alert" style={{ backgroundColor: t.errorBg, color: t.error, borderRadius: '12px' }}>{error}</div>;
  }

  if (episodes.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-play-circle" style={{ fontSize: '3rem', color: t.textMuted }}></i>
        <p className="mt-2" style={{ color: t.textMuted }}>Belum ada episode tersedia.</p>
      </div>
    );
  }

  const completedCount = episodes.filter(ep => ep.is_completed).length;
  const totalCount = episodes.length;

  return (
    <div style={{ animation: 'slide-in-up 0.5s ease' }}>
      <div className="d-flex align-items-center gap-2 mb-3">
        <TrophyIcon size={28} />
        <h5 style={{ color: t.gold, fontWeight: 700, margin: 0 }}>Daftar Episode</h5>
      </div>

      <div className="d-flex gap-2 mb-3">
        <span style={{
          fontSize: '0.8rem', fontWeight: 600,
          background: `linear-gradient(135deg, ${t.gold}22, rgba(255,140,0,0.1))`,
          border: `1px solid ${t.gold}33`, color: t.gold,
          borderRadius: '20px', padding: '4px 14px',
        }}>
          <i className="bi bi-check-circle me-1"></i>{completedCount}/{totalCount} Selesai
        </span>
        {completedCount === totalCount && totalCount > 0 && (
          <span style={{
            fontSize: '0.8rem', fontWeight: 700,
            background: 'linear-gradient(135deg, #22C55E22, #22C55E11)',
            border: '1px solid #22C55E44', color: t.success,
            borderRadius: '20px', padding: '4px 14px',
          }}>
            <i className="bi bi-stars me-1"></i>Semua Lengkap!
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {episodes.map((ep, index) => {
          const badgeColor = index < 3 ? podiumColors[index] : t.incompleteBorder;
          const isCompleted = ep.is_completed;
          return (
            <div
              key={ep.id}
              className="d-flex align-items-center justify-content-between animate-slide-up"
              style={{
                animationDelay: `${index * 0.06}s`,
                background: isCompleted ? `${t.gold}08` : t.cardBg,
                border: isCompleted ? `1px solid ${t.gold}20` : `1px solid ${t.borderLight}`,
                borderRadius: '12px', padding: '14px 16px', color: t.text,
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = `${t.gold}55`;
                e.currentTarget.style.transform = 'translateX(4px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = isCompleted ? `${t.gold}20` : t.borderLight;
                e.currentTarget.style.transform = 'translateX(0)';
              }}
            >
              <div className="d-flex align-items-center">
                {isCompleted ? (
                  <span className="badge rounded-circle me-3" style={{
                    width: '30px', height: '30px', lineHeight: '22px',
                    background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff',
                  }}>
                    <i className="bi bi-check"></i>
                  </span>
                ) : (
                  <span className="badge rounded-circle me-3" style={{
                    width: '30px', height: '30px', lineHeight: '22px',
                    background: badgeColor,
                    color: index < 3 ? t.bg : t.textMuted, fontWeight: 700,
                  }}>
                    {index + 1}
                  </span>
                )}
                <div>
                  <strong style={{ color: t.text }}>{ep.title}</strong>
                  {ep.description && <p className="mb-0" style={{ fontSize: '0.8rem', color: t.textMuted }}>{ep.description}</p>}
                  {ep.duration && <small style={{ color: t.textMuted }}><i className="bi bi-clock me-1"></i>{ep.duration} menit</small>}
                </div>
              </div>
              <div className="d-flex gap-2">
                {ep.video_url && (
                  <Link to={`/learning/${webinarId}/episodes/${ep.id}`} className="btn btn-sm" style={{
                    background: 'transparent', border: `1.5px solid ${t.gold}`, color: t.gold,
                    fontWeight: 600, borderRadius: '10px', padding: '6px 16px', transition: 'all 0.3s ease',
                  }}>
                    <i className="bi bi-play-fill me-1"></i>Tonton
                  </Link>
                )}
                {!isCompleted && (
                  <button className="btn btn-sm" style={{
                    background: `linear-gradient(135deg, ${t.success}, #16A34A)`, border: 'none',
                    color: '#fff', fontWeight: 600, borderRadius: '10px', padding: '6px 14px',
                  }} onClick={() => handleMarkComplete(ep.id)}>
                    <i className="bi bi-check-lg me-1"></i>Selesai
                  </button>
                )}
                {isCompleted && (
                  <span style={{ color: t.success, fontSize: '0.85rem', fontWeight: 600 }}>
                    <i className="bi bi-check-circle me-1"></i>Selesai
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
